import Vue, { CreateElement } from '@src/vue'
import { Component, Watch } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'

import Member from '@src/data/member'
import { BMI } from '@src/routes/members/components/body-measurements/bmi'
import { Filter } from '@src/routes/content/components/filter'
import { WeightData } from '@src/routes/members/components/body-measurements/weight-data'
import { BloodPressure } from '@src/routes/members/components/body-measurements/blood-pressure'
import { Circumferences } from '@src/routes/members/components/body-measurements/circumferences'
import { BodyComposition } from '@src/routes/members/components/body-measurements/body-composition'
import { BodyMeasurements, MetricData } from '@src/data/body-measurement'
import ApiClient from '@src/lib/api_client'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { API } from '@src/lib/api_path'
import EventBus from '@src/lib/event_bus'
import { isMobileBrowser, track } from '@src/util/functions'

import './measurements.sass'
import { convertLengthForDisplaying, convertWeightForDisplaying, LengthUnit, WeightUnit } from '@src/util/weight'
import { MemberProfile } from '@src/data/member-profile'
import { Button } from '@src/elements/button/button'

type BodyMeasurementsResponse = { profile: BodyMeasurements }

let metricsThatRequireConversion = ['trunk_muscle_mass', 'trunk_fat_mass', 'left_arm_muscle_mass', 'left_arm_fat_mass',
  'right_arm_muscle_mass', 'right_arm_fat_mass', 'left_leg_muscle_mass', 'left_leg_fat_mass', 'right_leg_muscle_mass', 'right_leg_fat_mass']

@Component({
  computed: {
    member () {
      return this.$store.state.selectedMember
    },
    gender () {
      return this.$store.state.selectedMember.gender
    },
    weightUnit () {
      return this.$store.state.club.weightUnit
    },
    lengthUnit () {
      return this.$store.state.club.weightUnit === WeightUnit.KG ? LengthUnit.CM : LengthUnit.IN
    }
  }
})
export class Measurements extends Vue {

  member: Member
  gender: string

  protected weightUnit: WeightUnit

  protected apiClient: ApiClient = new ApiClient()
  protected loading: boolean = true

  protected bodyMeasurements: BodyMeasurements | MemberProfile | null = null

  protected measurementFilters = ['body_mass_index', 'weight_data', 'blood_pressure', 'body_composition', 'circumferences']
  protected measurements: string[] = Object.assign([], this.measurementFilters)
  private hidePrintOnMobileLandscapeOrientation: boolean
  private lengthUnit: LengthUnit

  constructor () {
    super()
    track('/members/body-measurements')
  }

  @Watch('bodyMeasurements')
  forceUpdateOnBodyMeasurementsChange () {
    this.$forceUpdate()
  }

  showMeasurement (type: string) {
    const index = this.measurements.indexOf(type)
    if (index >= 0) {
      this.measurements.splice(index, 1)
    } else {
      this.measurements.push(type)
    }
  }

  clearAllMeasurements () {
    this.measurements = []
  }

  async print () {
    window.print()
  }

  render (h: CreateElement) {
    if (this.loading) {
      return <Loader />
    }
    return <div class='body-measurements-wrapper scroll-content'>
      <div class='body-measurements-filter'>
        <Filter tag={'body-measurements'} ref='bodyMeasurementsFilter'
                filterList={this.measurementFilters}
                placeholder={this.$t('BODY_MEASUREMENTS.BODY_MEASUREMENTS_PLACEHOLDER')}
                translation={'BODY_MEASUREMENTS.'}
                selectedFilters={new Set(this.measurements)}
                filterCallback={this.showMeasurement}
                clearAllCallback={this.clearAllMeasurements} />
        {!this.hidePrintOnMobileLandscapeOrientation ? <Button type='remove' click={this.print} icon={'util/print'} label={this.$t('PRINT')}
                class='print-btn' /> : ''}
      </div>

      {this.renderBmi(h)}
      {this.renderWeightData(h)}
      {this.renderBloodPressure(h)}
      {this.renderBodyComposition(h)}
      {this.renderCircumferences(h)}
    </div>
  }

  renderBmi (h: CreateElement) {
    return this.measurements.includes('body_mass_index') ?
      <BMI gender={this.gender}
           height={this.bodyMeasurements!.metrics['height'] || []}
           weight={this.bodyMeasurements!.metrics['body_weight'] || []} /> : null
  }

  convertMeasurementsToLocalUnit (values) {
    return values.map((measurement) => {
      return { ...measurement, value: convertWeightForDisplaying(measurement.value, this.weightUnit, 2) }
    })
  }

  renderWeightData (h: CreateElement) {
    return this.measurements.includes('weight_data') ?
      <WeightData
        measurements={{
          bodyWeight: this.convertMeasurementsToLocalUnit(this.bodyMeasurements!.metrics['body_weight'] || []),
          bodyFat: this.convertMeasurementsToLocalUnit(this.bodyMeasurements!.metrics['body_fat'] || []),
          muscleMass: this.convertMeasurementsToLocalUnit(this.bodyMeasurements!.metrics['muscle_mass'] || []),
          bodyWaterMass: this.convertMeasurementsToLocalUnit(this.bodyMeasurements!.metrics['body_water_mass'] || []),
          bodyVfat: this.convertMeasurementsToLocalUnit(this.bodyMeasurements!.metrics['body_vfat'] || [])
        }}
        goals={this.bodyMeasurements!.goals}
        bodyWeightNotes={this.bodyMeasurements!.bodyWeightNotes || ''} /> : null
  }

  renderBloodPressure (h: CreateElement) {
    return this.measurements.includes('blood_pressure') ?
      <BloodPressure
        measurements={{
          systolicBloodPressure: this.bodyMeasurements!.metrics['systolic_blood_pressure'] || [],
          diastolicBloodPressure: this.bodyMeasurements!.metrics['diastolic_blood_pressure'] || []
        }}
        bloodPressureNotes={this.bodyMeasurements!.bloodPressureNotes || ''} /> : null
  }

  renderBodyComposition (h: CreateElement) {

    let convertedValues = {}
    metricsThatRequireConversion.forEach((metric) => {
      convertedValues[metric] = this.convertMeasurementsToLocalUnit(this.bodyMeasurements!.metrics[metric] || [])
    })
    return this.measurements.includes('body_composition') ?
      <BodyComposition measurements={{
        trunkMuscleMass: convertedValues['trunk_muscle_mass'],
        trunkMusclePercentage: this.bodyMeasurements!.metrics['trunk_muscle_percentage'] || [],
        trunkFatMass: convertedValues['trunk_fat_mass'],
        trunkFatPercentage: this.bodyMeasurements!.metrics['trunk_fat_percentage'] || [],
        leftArmMuscleMass: convertedValues['left_arm_muscle_mass'],
        leftArmMusclePercentage: this.bodyMeasurements!.metrics['left_arm_muscle_percentage'] || [],
        leftArmFatMass: convertedValues['left_arm_fat_mass'],
        leftArmFatPercentage: this.bodyMeasurements!.metrics['left_arm_fat_percentage'] || [],
        rightArmMuscleMass: convertedValues['right_arm_muscle_mass'],
        rightArmMusclePercentage: this.bodyMeasurements!.metrics['right_arm_muscle_percentage'] || [],
        rightArmFatMass: convertedValues['right_arm_fat_mass'],
        rightArmFatPercentage: this.bodyMeasurements!.metrics['right_arm_fat_percentage'] || [],
        leftLegMuscleMass: convertedValues['left_leg_muscle_mass'],
        leftLegMusclePercentage: this.bodyMeasurements!.metrics['left_leg_muscle_percentage'] || [],
        leftLegFatMass: convertedValues['left_leg_fat_mass'],
        leftLegFatPercentage: this.bodyMeasurements!.metrics['left_leg_fat_percentage'] || [],
        rightLegMuscleMass: convertedValues['right_leg_muscle_mass'],
        rightLegMusclePercentage: this.bodyMeasurements!.metrics['right_leg_muscle_percentage'] || [],
        rightLegFatMass: convertedValues['right_leg_fat_mass'],
        rightLegFatPercentage: this.bodyMeasurements!.metrics['right_leg_fat_percentage'] || []
      }} /> : null
  }

  renderCircumferences (h: CreateElement) {
    return this.measurements.includes('circumferences') ?
      <Circumferences
        measurements={{
          waistline: this.bodyMeasurements!.metrics['waistline'] ? this.convertToLengthUnit(this.bodyMeasurements!.metrics['waistline']) : [],
          rightBicepCircumference: this.bodyMeasurements!.metrics['right_bicep_circumference'] ? this.convertToLengthUnit(this.bodyMeasurements!.metrics['right_bicep_circumference']) : [],
          leftBicepCircumference: this.bodyMeasurements!.metrics['left_bicep_circumference'] ? this.convertToLengthUnit(this.bodyMeasurements!.metrics['left_bicep_circumference']) : [],
          rightThighCircumference: this.bodyMeasurements!.metrics['right_thigh_circumference'] ? this.convertToLengthUnit(this.bodyMeasurements!.metrics['right_thigh_circumference']) : [],
          leftThighCircumference: this.bodyMeasurements!.metrics['left_thigh_circumference'] ? this.convertToLengthUnit(this.bodyMeasurements!.metrics['left_thigh_circumference']) : [],
          hipMeasurement: this.bodyMeasurements!.metrics['hip_measurement'] ? this.convertToLengthUnit(this.bodyMeasurements!.metrics['hip_measurement']) : [],
          chestCircumference: this.bodyMeasurements!.metrics['chest_circumference'] ? this.convertToLengthUnit(this.bodyMeasurements!.metrics['chest_circumference']) : []
        }}
        goals={this.bodyMeasurements!.goals}
        bodyCircumferenceNotes={this.bodyMeasurements!.bodyCircumferenceNotes || ''} /> : null
  }

  convertToLengthUnit (metricData: MetricData[]) {
    return metricData.map((metricUnit) => {
      return { ...metricUnit, value: convertLengthForDisplaying(metricUnit.value, this.lengthUnit, 2) }
    })
  }

  created () {
    EventBus.on('reloadBodyMeasurements', this.loadBodyMeasurements)
    window.addEventListener('orientationchange', this.updatePrintingSettings)
    window.addEventListener('resize', this.updatePrintingSettings)
    this.updatePrintingSettings()

    return this.loadBodyMeasurements()
  }

  updated () {
    this.setDocumentTitle()
  }

  beforeDestroy () {
    EventBus.off('reloadBodyMeasurements', this.loadBodyMeasurements)
  }

  destroyed () {
    window.removeEventListener('orientationchange', this.updatePrintingSettings)
    window.removeEventListener('resize', this.updatePrintingSettings)
  }

  setDocumentTitle () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MEMBERS')} - ${this.$root.$t('PAGE.MEMBERS.DETAIL.TABS.BODY_MEASUREMENTS')}`
  }

  updatePrintingSettings () {
    const isLandscapeOrientation = window.matchMedia('(orientation: landscape)').matches
    const newPrintSettings = isMobileBrowser()
    && isLandscapeOrientation

    if (this.hidePrintOnMobileLandscapeOrientation !== newPrintSettings) {
      this.hidePrintOnMobileLandscapeOrientation = newPrintSettings
    }
    this.$forceUpdate()
  }

  protected loadBodyMeasurements () {
    return this.apiClient.get<BodyMeasurementsResponse>(API.TRAINERS_API.BODY_MEASUREMENTS.replace('{memberId}', `${this.member.id}`))
      .then((response) => {
        this.bodyMeasurements = new BodyMeasurements(camelcaseKeys(response.profile, { deep: true }))
      })
      .catch((error) => console.error('failed to retrieve body measurements', error))
      .finally(() => this.loading = false)
  }
}
